import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { useSiteT } from "../../hooks/useSiteT";

// Admin-entered defaults are English; when they still equal the seeded text we localize them.
const norm = (v) => String(v || "").toLowerCase().replace(/[^a-z]/g, "");
const isDefaultScope = (v) => norm(v).startsWith("managedadvertisingoperations");
const isDefaultPricing = (v) => norm(v).startsWith("feestartsat3");
const isDefaultGuarantee = (v) => norm(v).startsWith("platformapprovalsandaccountstatus");

export default function ComplianceInfoSection() {
    const { language } = useLanguage();
    const { settings } = useSiteSettings();
    const { t } = useSiteT();

    const c = {
        en: {
            title: "Company & Service Information",
            scope: "Service scope",
            legal: "Legal entity",
            guarantee: "Policy notice",
            pricing: "Pricing transparency",
        },
        pt: {
            title: "Informações da Empresa e Serviço",
            scope: "Escopo do serviço",
            legal: "Entidade legal",
            guarantee: "Aviso de política",
            pricing: "Transparência de preços",
        },
        es: {
            title: "Información de Empresa y Servicio",
            scope: "Alcance del servicio",
            legal: "Entidad legal",
            guarantee: "Aviso de políticas",
            pricing: "Transparencia de precios",
        },
        ru: {
            title: "Информация о компании и сервисе",
            scope: "Объем сервиса",
            legal: "Юр. лицо",
            guarantee: "Policy-уведомление",
            pricing: "Прозрачность цен",
        },
        tr: {
            title: "Şirket ve Hizmet Bilgileri",
            scope: "Hizmet kapsamı",
            legal: "Yasal şirket",
            guarantee: "Politika notu",
            pricing: "Fiyat şeffaflığı",
        },
    }[language] || {
        title: "Company & Service Information",
        scope: "Service scope",
        legal: "Legal entity",
        guarantee: "Policy notice",
        pricing: "Pricing transparency",
    };

    return (
        <section
            data-testid="compliance-info-section"
            className="py-14 border-y border-white/[0.07] bg-white/[0.01]"
        >
            <div className="container-skala grid lg:grid-cols-12 gap-5">
                <article
                    data-testid="compliance-company-card"
                    className="lg:col-span-5 rounded-2xl border border-white/[0.08] bg-card p-6"
                >
                    <h3 className="text-base md:text-lg font-semibold">{c.title}</h3>
                    <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                        <p>
                            <span className="text-foreground/80">{c.legal}:</span>{" "}
                            {settings?.company_legal_name || settings?.company_name || "Adrise"}
                        </p>
                        <p>
                            <span className="text-foreground/80">Email:</span>{" "}
                            {settings?.contact_email || "support@adrise-agency.com"}
                        </p>
                        <p>
                            <span className="text-foreground/80">
                                {t("ci.phone")}:
                            </span>{" "}
                            {settings?.contact_phone || t("ci.phoneDefault")}
                        </p>
                        <p className="whitespace-pre-line" data-testid="compliance-company-address">
                            <span className="text-foreground/80">
                                {t("ci.address")}:
                            </span>{" "}
                            {settings?.company_address || t("ci.addressDefault")}
                        </p>
                    </div>
                </article>

                <article
                    data-testid="compliance-policy-card"
                    className="lg:col-span-7 rounded-2xl border border-white/[0.08] bg-card p-6"
                >
                    <h3 className="text-base md:text-lg font-semibold">{c.scope}</h3>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="compliance-service-scope">
                        {settings?.service_scope ? (isDefaultScope(settings.service_scope) ? t("ci.scopeDefault") : settings.service_scope) : t("ci.scopeDefault")}
                    </p>
                    <div className="mt-5 space-y-2 text-sm">
                        <p data-testid="compliance-pricing-note">
                            <span className="text-foreground/80">{c.pricing}:</span>{" "}
                            <span className="text-muted-foreground">
                                {settings?.pricing_transparency_note && !isDefaultPricing(settings.pricing_transparency_note) ? settings.pricing_transparency_note : t("ci.pricingDefault")}
                            </span>
                        </p>
                        <p data-testid="compliance-guarantee-note">
                            <span className="text-foreground/80">{c.guarantee}:</span>{" "}
                            <span className="text-muted-foreground">
                                {settings?.guarantee_note && !isDefaultGuarantee(settings.guarantee_note) ? settings.guarantee_note : t("ci.guaranteeDefault")}
                            </span>
                        </p>
                    </div>
                </article>
            </div>
        </section>
    );
}
